import { CircleNotchIcon, EyeIcon } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { type PortalPreview, useEndPortalPreview } from "@/lib/api";

/**
 * The persistent band over the portal while a staff member previews it read-only: which
 * client, when it ends, and Exit preview. When the hour runs out the preview is re-read,
 * and the portal guard sends the staff member back to the cockpit.
 */
export function PreviewBanner({ preview }: { preview: PortalPreview }) {
  const end = useEndPortalPreview();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const expiresAt = new Date(preview.expires_at);

  useEffect(() => {
    const timer = window.setTimeout(
      () => void queryClient.invalidateQueries({ queryKey: ["portal", "preview"] }),
      Math.max(0, new Date(preview.expires_at).getTime() - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [preview.expires_at, queryClient]);

  const exit = () =>
    end.mutate(undefined, {
      onSettled: () => void navigate(`/cockpit/engagements/${preview.engagement_id}`),
    });

  return (
    <div
      role="status"
      className="bg-primary text-primary-foreground sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-12"
    >
      <p className="flex items-center gap-2 text-sm leading-5">
        <EyeIcon className="h-4 w-4 shrink-0" />
        <span>
          Previewing <strong className="font-medium">{preview.workspace_name}</strong>&apos;s
          portal, read-only, until{" "}
          {expiresAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.
        </span>
      </p>
      <Button variant="secondary" size="sm" onClick={exit} disabled={end.isPending}>
        {end.isPending ? <CircleNotchIcon className="animate-spin" /> : null}
        Exit preview
      </Button>
    </div>
  );
}
